import { useCallback, useEffect, useReducer, useRef } from "react";

import { useWizard } from "@performyard/py-components";

import type {
	AddFormsStepData,
	ConfiguredForm,
	FormDataByType,
	FormType,
	FormsByType,
	SelectedTemplate,
} from "./types";

import type { TemplateFormAssignmentWithNames } from "../../../../../../api/services/defs/review-templates";

import { MAX_FORMS_PER_TYPE, STEP_ID } from "./constants";
import { createAdHocForm } from "./createDefaultForm";
import { templateAssignmentsToConfiguredForms } from "./services/transformAssignments";

const EMPTY_STATE: AddFormsStepData = {
	selectedTemplate: null,
	forms: { standard: [], "360": [], external: [] },
};

type Action =
	| { type: "setSelectedTemplate"; payload: SelectedTemplate }
	| {
			type: "applyTemplateAssignments";
			payload: { templateId: string; assignments: TemplateFormAssignmentWithNames[] };
	  }
	| { type: "clearTemplate" }
	| {
			type: "addAdHocForms";
			payload: { formType: FormType; seeds: Array<{ formId: string; formName: string }>; cycleStartDate: Date };
	  }
	| {
			type: "updateForm";
			payload: { formType: FormType; localId: string; data: FormDataByType[FormType] };
	  }
	| { type: "removeForm"; payload: { formType: FormType; localId: string } };

const onlyAdHoc = (forms: FormsByType): FormsByType => ({
	standard: forms.standard.filter((f) => !f.fromTemplate),
	"360": forms["360"].filter((f) => !f.fromTemplate),
	external: forms.external.filter((f) => !f.fromTemplate),
});

const reducer = (state: AddFormsStepData, action: Action): AddFormsStepData => {
	switch (action.type) {
		case "setSelectedTemplate":
			// Switching templates: preserve ad-hoc forms; template forms get repopulated
			// once the fetch resolves and `applyTemplateAssignments` fires.
			return {
				selectedTemplate: action.payload,
				forms: onlyAdHoc(state.forms),
			};

		case "applyTemplateAssignments": {
			// Defensive: only apply if the in-flight fetch matches the currently selected template.
			if (state.selectedTemplate?.id !== action.payload.templateId) return state;

			const templateForms = templateAssignmentsToConfiguredForms(action.payload.assignments);
			const adHoc = onlyAdHoc(state.forms);
			return {
				selectedTemplate: state.selectedTemplate,
				forms: {
					standard: [...templateForms.standard, ...adHoc.standard],
					"360": [...templateForms["360"], ...adHoc["360"]],
					external: [...templateForms.external, ...adHoc.external],
				},
			};
		}

		case "clearTemplate":
			return { selectedTemplate: null, forms: onlyAdHoc(state.forms) };

		case "addAdHocForms": {
			const { formType, seeds, cycleStartDate } = action.payload;
			const existing = state.forms[formType];
			const remainingSlots = Math.max(0, MAX_FORMS_PER_TYPE - existing.length);
			const newForms = seeds
				.slice(0, remainingSlots)
				.map((seed) => createAdHocForm(formType, seed, cycleStartDate));

			return {
				...state,
				forms: {
					...state.forms,
					[formType]: [...existing, ...newForms],
				} as FormsByType,
			};
		}

		case "updateForm": {
			const { formType, localId, data } = action.payload;
			return {
				...state,
				forms: {
					...state.forms,
					[formType]: (state.forms[formType] as ConfiguredForm[]).map((f) =>
						f.localId === localId ? ({ ...f, data } as ConfiguredForm) : f
					),
				} as FormsByType,
			};
		}

		case "removeForm": {
			const { formType, localId } = action.payload;
			return {
				...state,
				forms: {
					...state.forms,
					[formType]: state.forms[formType].filter((f) => f.localId !== localId),
				} as FormsByType,
			};
		}

		default:
			return state;
	}
};

export const useAddFormsState = () => {
	const { wizardData, setStepData } = useWizard();

	const initialState = (wizardData[STEP_ID] as AddFormsStepData | undefined) ?? EMPTY_STATE;
	const [state, dispatch] = useReducer(reducer, initialState);

	const lastSyncedRef = useRef(initialState);
	useEffect(() => {
		if (state !== lastSyncedRef.current) {
			lastSyncedRef.current = state;
			setStepData(STEP_ID, state);
		}
	}, [state, setStepData]);

	const setSelectedTemplate = useCallback(
		(template: SelectedTemplate) => dispatch({ type: "setSelectedTemplate", payload: template }),
		[]
	);

	const applyTemplateAssignments = useCallback(
		(templateId: string, assignments: TemplateFormAssignmentWithNames[]) =>
			dispatch({ type: "applyTemplateAssignments", payload: { templateId, assignments } }),
		[]
	);

	const clearTemplate = useCallback(() => dispatch({ type: "clearTemplate" }), []);

	const addAdHocForms = useCallback(
		(formType: FormType, seeds: Array<{ formId: string; formName: string }>, cycleStartDate: Date) =>
			dispatch({ type: "addAdHocForms", payload: { formType, seeds, cycleStartDate } }),
		[]
	);

	const updateForm = useCallback(
		(formType: FormType, localId: string, data: FormDataByType[FormType]) =>
			dispatch({ type: "updateForm", payload: { formType, localId, data } }),
		[]
	);

	const removeForm = useCallback(
		(formType: FormType, localId: string) => dispatch({ type: "removeForm", payload: { formType, localId } }),
		[]
	);

	const totalCount = state.forms.standard.length + state.forms["360"].length + state.forms.external.length;

	return {
		state,
		totalCount,
		setSelectedTemplate,
		applyTemplateAssignments,
		clearTemplate,
		addAdHocForms,
		updateForm,
		removeForm,
	};
};

export { reducer as _reducer, EMPTY_STATE as _EMPTY_STATE };
